import { StepperSelectionEvent } from '@angular/cdk/stepper';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import format from 'date-fns/format';
import { Subscription } from 'rxjs';
import { AutenticacaoService } from 'src/app/seguranca/autenticacao.service';
import { Bandeira } from 'src/app/shared/models/bandeira.enum';
import { Contrato } from 'src/app/shared/models/contrato.model';
import { EnviaLinhaDigitalModalData } from 'src/app/shared/models/envia-linha-digitavel-modal-data';
import { EnviaEmailBoletoRequest } from 'src/app/shared/models/enviar-email-boleto-request.model';
import { EnviaSmsBoletoRequest } from 'src/app/shared/models/enviar-sms-boleto-request.model';
import { TipoPlanoEnum } from 'src/app/shared/models/novo-plano-renegociacao.model';
import { PagamentoCeler, TipoContratoEnum } from 'src/app/shared/models/pagamento-pix-celer-model';
import { Usuario } from 'src/app/shared/models/usuario.model';
import { GoogleAnalyticsService } from 'src/app/shared/services/google-analytics.service';
import DateUtils from 'src/app/shared/utils/date-utils';
import StringUtils from 'src/app/shared/utils/string-utils';
import { environment } from 'src/environments/environment';
import { StatusParcelaEnum } from './../../shared/enums/status-parcela.enum';
import { LoaderService } from './../../shared/loader/loader.service';
import { MensagensService } from './../../shared/mensagens/mensagens.service';
import { Parcela } from './../../shared/models/parcela.model';
import { SegundaViaResponse } from './../../shared/models/segunda-via-response.model';
import { ContratoService } from './../contrato.service';

@Component({
  selector: 'app-segunda-via-parcela-page',
  templateUrl: './segunda-via-parcela-page.component.html',
  styleUrls: ['./segunda-via-parcela-page.component.scss']
})
export class SegundaViaParcelaPageComponent implements OnInit, OnDestroy {

  subscriptions = new Subscription();
  idContrato: string | null;
  parcela: Parcela | null = null;
  segundaVia: SegundaViaResponse | null = null;
  disabledSms = false;
  disabledEmail = false;
  bandeira = this.autenticacaoService.bandeira;
  contrato: Contrato;
  permitirPagamentoPix = false;

  private hostname = window.location.hostname;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private autenticacaoService: AutenticacaoService,
    private contratoService: ContratoService,
    private loader: LoaderService,
    private googleAnalyticsService: GoogleAnalyticsService,
    private mensagems: MensagensService,
  ) { }

  ngOnInit(): void {
    this.subscriptions.add(this.route.params.subscribe(params => {
      const { idContrato, numeroParcela } = params;
      this.idContrato = idContrato;
      this.buscarParcelaContrato(idContrato, parseInt(numeroParcela, 10));
      this.buscarSegundaVia(idContrato, numeroParcela);
      this.buscarContrato(idContrato);
      this.verificarPermissaoBotaoPix(idContrato);
    }));
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  get showBanqi() {
    return (this.checkHostName() || environment.banqi) && this.bandeira !== Bandeira.PONTOFRIO;
  }

  async getTotalParcelas() {
    const { parcelas } = await this.contratoService.getContrato(this.idContrato);

    return parcelas.length;
  }

  get showAlertBoleto(): boolean {
    return this.parcela &&
      (this.parcela.statusParcela === StatusParcelaEnum.STATUS_VENCIDA ||
        this.parcela.statusParcela === StatusParcelaEnum.STATUS_ACORDO_RENEGOCIADO);
  }

  private async buscarParcelaContrato(idContrato: string, numeroParcela: number): Promise<void> {
    try {
      this.loader.start();
      this.parcela = await this.contratoService.getParcela(idContrato, numeroParcela);
    } catch (error) {
      this.mensagems.erro('Erro ao buscar a parcela. Por favor tente novamente mais tarde.');
    } finally {
      this.loader.stop();
    }
  }

  private async buscarSegundaVia(idContrato: string, numeroParcela: number): Promise<void> {
    try {
      this.loader.start();
      const res = await this.contratoService.getSegundaVia(idContrato, numeroParcela);

      this.segundaVia = {
        codigo: res?.linhaDigitavel,
        dataVencimento: res?.dataVencimento,
        valorDocumento: res?.valorDocumento,
        contaBoleto: res?.contaBoleto,
        numeroBoleto: res?.numeroBoleto,
      }
    } catch (error) {
      this.mensagems.erro('Erro ao buscar a segunda via. Por favor tente novamente mais tarde.');
    } finally {
      this.loader.stop();
    }
  }

  irParaGerarNovaParcela(): void {
    this.router.navigate(['/portal/contratos', this.idContrato, 'segundavia']);
  }

  async downloadBoleto(): Promise<void> {
    try {
      this.loader.start();
      await this.contratoService.downloadBoleto(this.idContrato, this.parcela.numeroParcela);
    } catch (error) {
      this.mensagems.erro('Erro ao efetuar download do boleto. Por favor tente novamente mais tarde.');
    } finally {
      this.loader.stop();
    }
  }

  voltarParaContratos({ selectedIndex }: StepperSelectionEvent): void {
    if (selectedIndex === 0) {
      this.router.navigate(['/portal/contratos']);
      return;
    }

    if (selectedIndex === 1) {
      this.router.navigate(['/portal/contratos', this.idContrato, 'segundavia']);
      return;
    }
  }

  async enviarBoletoEmailSms(modalData: EnviaLinhaDigitalModalData): Promise<void> {
    const { type, usuario } = modalData;

    try {
      this.loader.start();
      if (type === 'EMAIL') {
        await this.enviaBoletoEmail(usuario);
        this.disabledEmail = true;
      } else {
        await this.enviaBoletoSms(usuario);
        this.disabledSms = true;
      }
    } catch (error) {
      this.mensagems.erro(error.error.mensagem);
    } finally {
      this.loader.stop();
    }
  }

  private async enviaBoletoEmail(usuario: Usuario) {
    const enviaEmail: EnviaEmailBoletoRequest = {
      bandeira: this.bandeira,
      contrato: this.idContrato,
      email: usuario.email,
      nome: usuario.nome,
      parcela: this.parcela.numeroParcela,
      quantidadeParcela: await this.getTotalParcelas(),
    };

    return await this.contratoService.enviarBoletoEmailSegundaVia(enviaEmail);
  }

  private async enviaBoletoSms(usuario: Usuario) {
    const enviaSms: EnviaSmsBoletoRequest = {
      bandeira: this.bandeira,
      contrato: this.idContrato,
      cpf: usuario.cpf,
      phone: usuario.telefone,
      parcela: this.parcela.numeroParcela,
    };

    return await this.contratoService.enviarBoletoSmsSegundaVia(enviaSms);
  }

  private checkHostName(): boolean {
    return this.hostname.indexOf('casasbahia') > 0 || this.hostname.indexOf('cb') > 0;
  }

  private async buscarContrato(idContrato: string): Promise<void> {
    this.contrato = await this.contratoService.getContrato(idContrato);
    this.enviarEventoGTM();
  }

  enviarEventoGTM() {
    this.googleAnalyticsService.pushEvent({
      event: 'update',
      pagina: {
        nomePagina: '/renegociacao/contratos/segundavia/boletos',
        templatePagina: 'renegociacao',
        contratos: {
          numeroContrato: this.contrato.contrato,
          dataVencimento: format(DateUtils.normalizedDate(this.contrato.vencimento), 'dd/MM/yyyy'),
          saldo: this.contrato.saldo,
          parcelasPagas: this.contrato.parcelas.reduce((acc, parcela) => parcela.statusParcela === StatusParcelaEnum.STATUS_PAGO ? acc += 1 : acc, 0),
          situacao: this.googleAnalyticsService.situacaoContratoPipe(this.contrato.situacao),
          mercadoria: this.contrato?.mercadoria?.mercadorias.join(' / '),
        }
      }
    });
  }

  async pagarEntradaComPix() {
    try {
      this.loader.start();
      const { codigoBoleto, contaBoleto } = this.extrairContaECodigoBoleto(this.segundaVia.codigo);

      const informacaoPagamento: PagamentoCeler = {
        contrato: this.contrato.contrato,
        bandeira: this.autenticacaoService.bandeira,
        parcelaPagamento: this.parcela.numeroParcela,
        quantidadeParcelas: this.contrato.parcelas.length,
        tipoContrato: TipoContratoEnum.SEGUNDA_VIA,
        tipoPlano: TipoPlanoEnum.PARCELADO,
        urlRedirecionamento: this.LINK_REDIRECIONAMENTO,
        valorParcelaOriginal: this.parcela.valorTotalParcela,
        valorPagamento: this.segundaVia.valorDocumento,
        codigoBoleto: this.segundaVia?.numeroBoleto?.toString() ?? codigoBoleto,
        contaBoleto: this.segundaVia?.contaBoleto?.toString() ?? contaBoleto,
        vencimentoParcela: this.parcela.dataVencimento,
      };

      const linkData = await this.contratoService.buscarLinkPagamentoPix(informacaoPagamento);
      window.open(linkData.url, '_self');
    } catch (response) {
      if (response && response.error && response.error.message) {
        this.mensagems.erro(response.error.message);
      } else {
        this.mensagems.erro('Erro ao gerar QrCode');
      }
    } finally {
      this.loader.stop();
    }
  }

  extrairContaECodigoBoleto(linhaDigitavel: string): { contaBoleto: string, codigoBoleto: string; } {
    const codigoNormalizado = StringUtils.removeSpecialCharacteres(linhaDigitavel);
    const contaBoleto = codigoNormalizado.slice(11, -27);
    const codigoBoleto = codigoNormalizado.slice(21, -17);
    return { contaBoleto, codigoBoleto };
  }

  get LINK_REDIRECIONAMENTO() {
    const path = window.location.origin;
    const token = btoa(`${this.contrato.contrato}:SEGUNDA_VIA:${this.autenticacaoService.token}`);
    return `${path}/#/?redirectToken=${token}`;
  }

  async verificarPermissaoBotaoPix(contratoId: string) {
    try {
      this.loader.start();
      const res = await this.contratoService.verificarElegibilidadePagamentoCeler(contratoId);
      this.permitirPagamentoPix = res.elegivel;
    } catch (error) {
      this.mensagems.erro(error.error.mensagem);
    } finally {
      this.loader.stop();
    }
  }

}
